import {
  Award,
  Bell,
  CreditCard,
  MessageSquare,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import type {
  NotificationData,
  NotificationType,
} from "@/types/notification";

interface NotificationMeta {
  icon: LucideIcon;
  /** Tailwind classes for the icon chip (light + dark). */
  iconWrap: string;
  label: string;
}

/** Per-type icon + colour, matching the contract's type list. */
export const NOTIFICATION_META: Record<NotificationType, NotificationMeta> = {
  SUBSCRIPTION: {
    icon: CreditCard,
    iconWrap:
      "bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400",
    label: "Subscription",
  },
  USER: {
    icon: UserPlus,
    iconWrap:
      "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-400",
    label: "User",
  },
  CONTACT: {
    icon: MessageSquare,
    iconWrap:
      "bg-blue-light-50 text-blue-light-500 dark:bg-blue-light-500/15 dark:text-blue-light-400",
    label: "Contact",
  },
  BELT_APPLICATION: {
    icon: Award,
    iconWrap:
      "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400",
    label: "Belt Application",
  },
  GENERAL: {
    icon: Bell,
    iconWrap:
      "bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400",
    label: "General",
  },
};

/**
 * Resolve the admin route a notification should navigate to.
 *
 * Live socket items carry a `data` object with record ids, so we can deep-link
 * to the exact record where a detail page exists (users, subscriptions). Stored
 * list items have no `data`, so they fall back to the section landing page.
 * GENERAL notifications don't navigate anywhere (returns null).
 */
export function getNotificationHref(
  type: NotificationType,
  data?: NotificationData | null,
): string | null {
  switch (type) {
    case "SUBSCRIPTION":
      return data?.subscriptionId
        ? `/subscriptions/${data.subscriptionId}`
        : "/subscriptions";
    case "USER":
      return data?.userId ? `/users/${data.userId}` : "/users";
    case "CONTACT":
      return "/support";
    case "BELT_APPLICATION":
      return "/users/approvals";
    case "GENERAL":
    default:
      return null;
  }
}

/** Compact relative time, e.g. "just now", "2m ago", "3h ago", "2d ago". */
export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";

  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 45) return "just now";

  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  return new Date(iso).toLocaleDateString();
}
